import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import {
  AlertCircle,
  ArrowLeft,
  Download,
  FileText,
  ListTree,
  Loader2,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { GradientSparkleIcon } from "@/components/extraction/GradientSparkleIcon"
import { UnifiedAiPanel } from "@/components/drafting/UnifiedAiPanel"
import { FormFieldCard } from "@/components/word-form/FormFieldCard"
import { CheckThisPill } from "@/components/word-form/ScheduleListPanel"
import { ScheduleListSheet } from "@/components/word-form/ScheduleListSheet"
import { useBidMembers } from "@/hooks/useBidMembers"
import { useBidReadOnly } from "@/hooks/useViewOnly"
import { ViewOnlyNotice } from "@/components/ViewOnlyNotice"
import { useAgentStore } from "@/stores/agent-store"
import { usePdfExportStore } from "@/stores/pdf-export-store"
import { pageCountText, withoutTryAgain } from "@/lib/output-version-text"
import { getOutputVersion, getWordFormExports } from "@/lib/api"
import { openWithFreshLink } from "@/lib/open-fresh-link"
import { UNSCHEDULED_KEY, useWordFormStore } from "@/stores/word-form-store"
import {
  fieldsInSchedule,
  isFillable,
  proposalForField,
  SCHEDULE_PARAM,
  fillUtterance,
  scheduleStatusLabel,
} from "@/lib/word-form"
import { cn } from "@/lib/utils"
import { DOCUMENT_SECTION_KEY } from "@/types/scope"
import type { FillProposal } from "@/types/schedule"
import type {
  FormField,
  FormSchedule,
  FormScheduleCorrection,
  WordFormIngestStatus,
} from "@/types/word-form"

// ===================
// Copy
// ===================

const INGEST_PILL: Record<
  WordFormIngestStatus,
  { label: string; variant: "warning" | "success" | "destructive" }
> = {
  pending: { label: "Reading", variant: "warning" },
  ready: { label: "Ready", variant: "success" },
  failed: { label: "Couldn't read", variant: "destructive" },
}

// ===================
// Helpers
// ===================

/**
 * The nav's progress, from the answers on screen rather than the last load:
 * accepting a field moves "2 of 5" at once. Manual-only schedules keep the
 * server's status — there is nothing here to fill.
 */
function withLocalProgress(
  schedule: FormSchedule,
  fields: FormField[],
  proposals: FillProposal[]
): FormSchedule {
  const fillable = fieldsInSchedule(fields, schedule.id).filter(isFillable)
  if (fillable.length === 0) return schedule
  const filled = fillable.filter(
    (f) => proposalForField(proposals, f.fieldId)?.status === "accepted"
  ).length
  const status: FormSchedule["status"] =
    filled === 0 ? "not_started" : filled === fillable.length ? "done" : "in_progress"
  return {
    ...schedule,
    status,
    fieldCounts: { ...schedule.fieldCounts, fillable: fillable.length, filled },
  }
}

/** A fresh link for one completed copy, from the exports list. */
async function freshCopyUrl(artifactId: string, outputId: string): Promise<string | null> {
  const { exports } = await getWordFormExports(artifactId)
  return exports.find((e) => e.id === outputId)?.downloadUrl ?? null
}

/** A fresh link for one PDF version. */
async function freshPdfUrl(artifactId: string, outputId: string): Promise<string | null> {
  const version = await getOutputVersion(artifactId, outputId)
  return version.status === "ready" ? version.downloadUrl : null
}

// ===================
// Page
// ===================

export function WordFormPage() {
  const { bidId, artifactId } = useParams<{ bidId: string; artifactId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const requestedSchedule = searchParams.get(SCHEDULE_PARAM)

  const {
    metadata,
    schedules,
    fields,
    headings,
    proposals,
    counts,
    exports,
    selectedScheduleId,
    isLoading,
    isExporting,
    isCorrecting,
    correctionError,
    clearCorrectionError,
    error,
    exportProblem,
    load,
    selectSchedule,
    refreshExports,
    decide,
    saveManualAnswer,
    acceptAllFromProfile,
    doExport,
    reingest,
    correctSchedules,
    reset,
  } = useWordFormStore()

  const [listOpen, setListOpen] = useState(false)
  /** Set by "Fill with AI"; sent once the panel's session is ready. */
  const [pendingFill, setPendingFill] = useState<string | null>(null)

  const bidMembers = useBidMembers(bidId)
  // A viewer, or a member not on the bid: every write here would be refused,
  // so the write and AI controls are withheld rather than left to fail.
  const { isReadOnly, isLoaded: isAccessLoaded } = useBidReadOnly(bidId)
  const canWrite = isAccessLoaded && !isReadOnly
  const resetAgentStore = useAgentStore((s) => s.reset)
  const isPanelOpen = useAgentStore((s) => s.isPanelOpen)
  const togglePanel = useAgentStore((s) => s.togglePanel)
  const sessionId = useAgentStore((s) => s.sessionId)
  const scopeArtifactId = useAgentStore((s) => s.activeScope?.artifactId ?? null)
  const isAiBusy = useAgentStore((s) => s.isStreaming || s.isPolling)

  useEffect(() => {
    if (!artifactId) return
    void load(artifactId)
    void refreshExports()
    return () => reset()
  }, [artifactId, load, refreshExports, reset])

  // Leaving the form must not carry its AI thread into the next surface.
  useEffect(() => {
    return () => resetAgentStore()
  }, [artifactId, resetAgentStore])

  // A deep link (`?schedule=`) wins once, as soon as the schedules are known.
  const appliedDeepLinkRef = useRef<string | null>(null)
  useEffect(() => {
    if (!requestedSchedule || appliedDeepLinkRef.current === requestedSchedule) return
    if (!schedules.some((s) => s.id === requestedSchedule)) return
    appliedDeepLinkRef.current = requestedSchedule
    selectSchedule(requestedSchedule)
  }, [requestedSchedule, schedules, selectSchedule])

  // Send the queued fill once the panel has committed THIS form's session.
  useEffect(() => {
    if (!pendingFill || !artifactId || !sessionId) return
    if (scopeArtifactId !== artifactId || isAiBusy) return
    setPendingFill(null)
    void useAgentStore.getState().sendTurn(pendingFill)
  }, [pendingFill, artifactId, sessionId, scopeArtifactId, isAiBusy])

  const navSchedules = useMemo(
    () => schedules.map((s) => withLocalProgress(s, fields, proposals)),
    [schedules, fields, proposals]
  )
  const unscheduledFields = useMemo(() => fieldsInSchedule(fields, null), [fields])
  const selected =
    selectedScheduleId === UNSCHEDULED_KEY
      ? null
      : navSchedules.find((s) => s.id === selectedScheduleId) ?? null
  const isUnscheduledView = selectedScheduleId === UNSCHEDULED_KEY
  const visibleFields = useMemo(
    () => fieldsInSchedule(fields, isUnscheduledView ? null : selected?.id ?? null),
    [fields, isUnscheduledView, selected?.id]
  )
  const fillableFields = visibleFields.filter(isFillable)
  const manualFields = visibleFields.filter((f) => !isFillable(f))
  const profilePending = fillableFields.filter((f) => {
    const proposal = proposalForField(proposals, f.fieldId)
    return proposal?.status === "pending" && proposal.source === "profile"
  }).length

  const backToWrite = () => void navigate(`/app/bids/${bidId}/write`)

  const handleFillWithAi = () => {
    if (!isPanelOpen) togglePanel()
    setPendingFill(fillUtterance(selected))
  }

  /** Accepted answers, written or not: what a completed copy is built from. */
  const acceptedCount = (counts?.accepted ?? 0) + (counts?.applied ?? 0)
  // The list holds the Word copies and their PDFs: the copy is the newest ready Word file.
  const latestCopy = exports.find((e) => e.status === "ready" && e.kind === "form_docx") ?? null
  const pdfEntry = usePdfExportStore((s) => (artifactId ? s.entries[artifactId] : undefined))
  const previewFormPdf = usePdfExportStore((s) => s.previewFormPdf)
  const isMakingPdf = pdfEntry?.phase === "converting"

  /**
   * PDF preview of the completed copy. Exports first when answers are
   * accepted — the server hands back the copy already made when nothing
   * changed — so the preview always shows the current answers.
   */
  const handlePdfPreview = async () => {
    if (!artifactId) return
    let docxId = latestCopy?.id ?? null
    if (acceptedCount > 0) {
      const result = await doExport()
      if (!result) return
      docxId = result.outputVersion.id
    }
    if (!docxId) return
    try {
      await previewFormPdf(artifactId, docxId)
    } catch {
      // The PDF line shows the failure and "Try again".
    }
  }

  const handleDownload = async () => {
    // Nothing accepted any more: hand back the newest completed copy.
    if (acceptedCount === 0 && latestCopy) {
      if (artifactId) void openWithFreshLink(() => freshCopyUrl(artifactId, latestCopy.id))
      return
    }
    const result = await doExport()
    if (!result) return
    window.open(result.outputVersion.downloadUrl, "_blank", "noopener,noreferrer")
    const notes: string[] = []
    if (result.pendingCount > 0) {
      notes.push(
        `${result.pendingCount} ${result.pendingCount === 1 ? "answer" : "answers"} still to review ${result.pendingCount === 1 ? "wasn't" : "weren't"} included.`
      )
    }
    // Only a fillable field has a card to carry the error; an answer whose field
    // is no longer on the form has nowhere to be marked, so say so instead.
    const cardIds = new Set(fields.filter(isFillable).map((f) => f.fieldId))
    const marked = result.rejected.filter((r) => cardIds.has(r.fieldId)).length
    const unmatched = result.rejected.length - marked
    if (marked > 0) {
      notes.push(
        `${marked} accepted ${marked === 1 ? "answer" : "answers"} couldn't be written — ${marked === 1 ? "it's" : "they're"} marked on the form.`
      )
    }
    if (unmatched > 0) {
      notes.push(
        `${unmatched} accepted ${unmatched === 1 ? "answer" : "answers"} no longer ${unmatched === 1 ? "matches" : "match"} a field on this form, so ${unmatched === 1 ? "it wasn't" : "they weren't"} included.`
      )
    }
    toast.success("Completed copy ready", {
      description: notes.length > 0 ? notes.join(" ") : undefined,
    })
  }

  // ── Ingest gates ──

  if (!metadata && isLoading) {
    return (
      <CentredState>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
      </CentredState>
    )
  }

  if (!metadata && error) {
    return (
      <CentredState>
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="max-w-md text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={() => artifactId && void load(artifactId)}>
          Try again
        </Button>
      </CentredState>
    )
  }

  if (metadata?.ingestStatus === "failed") {
    return (
      <CentredState>
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="max-w-md text-sm text-muted-foreground">
          {metadata.ingestError ?? "We couldn't read this form."}
        </p>
        <div className="flex items-center gap-2">
          {!isReadOnly && (
            <Button onClick={() => void reingest()} disabled={!canWrite}>
              Try again
            </Button>
          )}
          <Button variant="ghost" onClick={backToWrite}>
            Back to Write
          </Button>
        </div>
      </CentredState>
    )
  }

  const ingestStatus = metadata?.ingestStatus ?? "pending"
  const pill = INGEST_PILL[ingestStatus]
  const isPreparing = ingestStatus === "pending"
  const canDownload = acceptedCount > 0 || latestCopy !== null

  return (
    <div className="flex h-full flex-col">
      {/* Metadata strip */}
      <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-border bg-background px-6 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={backToWrite}
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="truncate text-sm font-medium">
            {metadata?.sourceFilename ?? "Buyer form"}
          </h1>
          <Badge variant={pill.variant} className="shrink-0 whitespace-nowrap text-[10px]">
            {pill.label}
          </Badge>
          {counts && (
            <span className="hidden shrink-0 text-meta md:inline">
              {counts.pending} to review · {counts.accepted} accepted · {counts.applied} in completed copy
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:inline-flex"
            onClick={togglePanel}
            aria-label={isPanelOpen ? "Hide AI assistant" : "Show AI assistant"}
            title={isPanelOpen ? "Hide AI assistant" : "Show AI assistant"}
          >
            {isPanelOpen ? (
              <PanelRightClose className="h-4 w-4" />
            ) : (
              <PanelRightOpen className="h-4 w-4" />
            )}
          </Button>
          {isReadOnly ? (
            // Exporting makes a new version — a write. A reader can still open the latest copy.
            latestCopy && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  artifactId && void openWithFreshLink(() => freshCopyUrl(artifactId, latestCopy.id))
                }
              >
                <Download className="mr-1.5 h-4 w-4" />
                Download latest copy
              </Button>
            )
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void handlePdfPreview()}
                disabled={!canWrite || isPreparing || isExporting || isMakingPdf || !canDownload}
              >
                <FileText className="mr-1.5 h-4 w-4" />
                PDF preview
              </Button>
              {/* Secondary: the page's one filled primary is "Fill with AI". */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void handleDownload()}
                disabled={!canWrite || isPreparing || isExporting || !canDownload}
                title={canDownload ? undefined : "Accept at least one answer first"}
              >
                {isExporting ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-1.5 h-4 w-4" />
                )}
                Download completed copy
              </Button>
            </>
          )}
        </div>
      </div>

      {isReadOnly && <ViewOnlyNotice className="border-b border-border px-6 py-2" />}

      {pdfEntry && (
        <FormPdfLine
          entry={pdfEntry}
          onRetry={() => void handlePdfPreview()}
          canRetry={canWrite}
          onOpen={(outputId) => artifactId && void openWithFreshLink(() => freshPdfUrl(artifactId, outputId))}
        />
      )}

      {exportProblem && (
        <div
          role="alert"
          className="flex items-center gap-2 border-b border-border bg-destructive/10 px-6 py-1.5 text-xs text-destructive"
        >
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1">{exportProblem.message}</span>
          {exportProblem.canRetry && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 text-foreground"
              onClick={() => void handleDownload()}
              disabled={isExporting}
            >
              Try again
            </Button>
          )}
        </div>
      )}

      {error && metadata && (
        <p
          role="alert"
          className="flex items-center gap-2 border-b border-border bg-destructive/10 px-6 py-2 text-xs text-destructive"
        >
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </p>
      )}

      {isPreparing ? (
        <CentredState>
          <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--ai-accent-text))]" />
          <p className="text-sm text-muted-foreground">Reading your form…</p>
          <p className="max-w-sm text-meta">
            We're finding the schedules and every field the buyer wants filled.
          </p>
        </CentredState>
      ) : (
        <div className="flex min-h-0 flex-1">
          {/* Schedules in this file */}
          <nav
            aria-label="Schedules in this file"
            className="flex w-64 shrink-0 flex-col border-r border-border"
          >
            <div className="min-h-0 flex-1 space-y-0.5 overflow-auto p-2">
              {navSchedules.map((schedule) => (
                <ScheduleNavItem
                  key={schedule.id}
                  schedule={schedule}
                  isActive={schedule.id === selected?.id}
                  onSelect={() => selectSchedule(schedule.id)}
                />
              ))}
              {unscheduledFields.length > 0 && (
                <button
                  type="button"
                  onClick={() => selectSchedule(UNSCHEDULED_KEY)}
                  aria-current={isUnscheduledView ? "true" : undefined}
                  className={cn(
                    "w-full rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                    isUnscheduledView
                      ? "bg-foreground/[0.07] font-medium text-foreground"
                      : "text-sidebar-foreground hover:bg-foreground/[0.05] hover:text-foreground"
                  )}
                >
                  {navSchedules.length > 0 ? "Other fields" : "Whole form"}
                  <span className="block text-meta font-normal">
                    {unscheduledFields.length} {unscheduledFields.length === 1 ? "field" : "fields"}
                  </span>
                </button>
              )}
            </div>
            <div className="border-t border-border p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => setListOpen(true)}
              >
                <ListTree className="mr-1.5 h-4 w-4" />
                {isReadOnly ? "Schedule list" : "Fix the schedule list"}
              </Button>
            </div>
          </nav>

          {/* Fields of the selected schedule */}
          <div className="min-w-0 flex-1 overflow-auto bg-card">
            <div className="mx-auto max-w-3xl px-6 py-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-medium">
                      {selected
                        ? selected.title
                        : navSchedules.length > 0
                          ? "Other fields"
                          : "Whole form"}
                    </h2>
                    {selected?.confidence === "low" && <CheckThisPill />}
                  </div>
                  {selected?.pageLabel && <p className="text-meta">{selected.pageLabel}</p>}
                </div>
                {!isReadOnly && (
                  <div className="flex shrink-0 items-center gap-2">
                    {profilePending > 0 && selected && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!canWrite}
                        onClick={() => void acceptAllFromProfile(selected.id)}
                      >
                        Accept all {profilePending} from profile
                      </Button>
                    )}
                    {/* The one leading AI action: black + sparkle. */}
                    <Button
                      size="sm"
                      onClick={handleFillWithAi}
                      disabled={
                        !canWrite || fillableFields.length === 0 || isAiBusy || pendingFill !== null
                      }
                    >
                      {isAiBusy || pendingFill !== null ? (
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      ) : (
                        <GradientSparkleIcon className="mr-1.5 h-4 w-4" />
                      )}
                      Fill with AI
                    </Button>
                  </div>
                )}
              </div>

              {visibleFields.length === 0 ? (
                <p className="mt-6 text-sm text-muted-foreground">
                  No fields found in this schedule.
                </p>
              ) : (
                <>
                  {fillableFields.length > 0 && (
                    <div className="mt-2 divide-y divide-border">
                      {fillableFields.map((field) => {
                        const proposal = proposalForField(proposals, field.fieldId)
                        return (
                          <FormFieldCard
                            key={field.fieldId}
                            field={field}
                            proposal={proposal}
                            onDecide={(decision, editedValue) => {
                              if (proposal) void decide(proposal.id, decision, editedValue)
                            }}
                            readOnly={!canWrite}
                            {...(canWrite
                              ? { onSaveManual: (draft: string) => saveManualAnswer(field, draft) }
                              : {})}
                          />
                        )
                      })}
                    </div>
                  )}

                  {manualFields.length > 0 && (
                    <section aria-label="Fill these in Word" className="mt-6 space-y-2">
                      <h3 className="text-sm font-medium">Fill these in Word</h3>
                      <p className="text-meta">
                        We leave these alone. Open the completed copy in Word to fill them.
                      </p>
                      <ul className="divide-y divide-border rounded-lg bg-muted/40 px-3">
                        {manualFields.map((field) => (
                          <li key={field.fieldId} className="py-2">
                            <p className="text-sm">{field.label}</p>
                            {field.supportReason && (
                              <p className="text-meta">{field.supportReason}</p>
                            )}
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                </>
              )}
            </div>
          </div>

          {/* AI assistant. Mounting it starts this form's session; "Fill with AI" talks to it. */}
          {isPanelOpen && (
            <aside
              className="hidden w-[360px] shrink-0 flex-col overflow-hidden border-l border-border bg-muted/25 lg:flex"
              data-ai-pane="true"
            >
              <UnifiedAiPanel
                key={artifactId ?? ""}
                bidId={bidId ?? ""}
                artifactId={artifactId ?? ""}
                sectionKey={DOCUMENT_SECTION_KEY}
                scopeKind="artifact"
                scopeLabel="This form"
                emptyHint="Ask about this form, or use Fill with AI to propose answers."
                bidMembers={bidMembers}
              />
            </aside>
          )}
        </div>
      )}

      <ScheduleListSheet
        open={listOpen}
        onOpenChange={setListOpen}
        filename={metadata?.sourceFilename ?? "Buyer form"}
        schedules={schedules}
        fields={fields}
        headings={headings}
        isBusy={isCorrecting}
        error={correctionError}
        onClearError={clearCorrectionError}
        {...(canWrite
          ? {
              onCorrect: (correction: FormScheduleCorrection) =>
                artifactId ? correctSchedules(artifactId, correction) : Promise.resolve(false),
            }
          : {})}
        onOpenSchedule={(scheduleId) => {
          selectSchedule(scheduleId)
          setListOpen(false)
        }}
      />
    </div>
  )
}

// ===================
// Sub-components
// ===================

type PdfEntry = NonNullable<ReturnType<typeof usePdfExportStore.getState>["entries"][string]>

/**
 * The completed copy's PDF preview: progress, then "about N pages" (a buyer
 * form's page count is always "about": Word lays it out differently) with the
 * PDF, or a plain failure with one "Try again".
 */
function FormPdfLine({
  entry,
  onRetry,
  canRetry,
  onOpen,
}: {
  entry: PdfEntry
  onRetry: () => void
  canRetry: boolean
  /** Opens the PDF with a fresh link. */
  onOpen: (outputId: string) => void
}) {
  if (entry.phase === "converting") {
    return (
      <p role="status" className="flex items-center gap-2 border-b border-border px-6 py-2 text-meta">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Making the PDF preview…
      </p>
    )
  }
  if (entry.phase === "ready") {
    const { version } = entry
    return (
      <p role="status" className="flex items-center gap-2 border-b border-border px-6 py-2 text-meta">
        <span>
          PDF preview ready
          {version.pageCount !== null && ` · ${pageCountText(version.pageCount, true)}`}
        </span>
        <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => onOpen(version.id)}>
          Open PDF
        </Button>
      </p>
    )
  }
  return (
    <div
      role="alert"
      className="flex items-center gap-2 border-b border-border bg-destructive/10 px-6 py-1.5 text-xs text-destructive"
    >
      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 flex-1">
        We couldn't make the PDF preview. {withoutTryAgain(entry.error)}
      </span>
      {canRetry && (
        <Button variant="ghost" size="sm" className="h-7 shrink-0 text-foreground" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  )
}

function CentredState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
      {children}
    </div>
  )
}

function ScheduleNavItem({
  schedule,
  isActive,
  onSelect,
}: {
  schedule: FormSchedule
  isActive: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={isActive ? "true" : undefined}
      className={cn(
        "w-full rounded-md px-2 py-1.5 text-left text-sm transition-colors",
        isActive
          ? "bg-foreground/[0.07] font-medium text-foreground"
          : "text-sidebar-foreground hover:bg-foreground/[0.05] hover:text-foreground"
      )}
    >
      <span className="block truncate">{schedule.title}</span>
      <span className="flex items-center gap-1.5 text-meta font-normal">
        {scheduleStatusLabel(schedule)}
        {schedule.confidence === "low" && (
          <span className="text-[hsl(var(--status-warning))]">· Check this</span>
        )}
      </span>
    </button>
  )
}
